// Formatting helpers shared across pages.

// Intl throws a RangeError on a currency code it cannot parse, and an empty one is
// the common case here rather than an exotic one: OCI reports `currency` as an
// optional field on a usage summary, so a row can arrive without it, and a report
// with no rows at all has nothing to take a currency from. A default parameter does
// not cover it — a caller passing an explicit '' overrides the default and still
// throws — so every formatter below goes through this instead.
//
// USD is the fallback because it is what the backend defaults to when it has nothing
// better (see the usage handlers). It is a display decision only: nothing stores or
// bills against a currency this invented.
const CURRENCY_FALLBACK = 'USD';
const currencyCode = (code) =>
  /^[A-Za-z]{3}$/.test(code || '') ? code : CURRENCY_FALLBACK;

// Compact money for dashboards: amounts of 1000 and up lose their cents so KPI
// tiles stay short. Do not use where a figure has to reconcile — it rounds, so
// 1234.56 renders as $1,235.
export const currency = (value, code = CURRENCY_FALLBACK) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode(code),
    maximumFractionDigits: value >= 1000 ? 0 : 2,
  }).format(value ?? 0);

// Money for a chart's y-axis ticks: short enough to fit the axis gutter, where
// "$12,345.67" would be wider than the space it has. A tick carries the order of
// magnitude; the cents live in the tooltip and the table.
export const currencyCompact = (value, code = CURRENCY_FALLBACK) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode(code),
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value ?? 0);

// Money with the cents always shown. Used on Billing and Cost & Billing, for
// both line amounts and totals, where every figure is something a client is
// charged or a provider invoiced.
//
// This is where money gets rounded, and it is the ONLY place that happens. The
// backend returns cost and billing at full precision — nothing in services/
// rounds (see priceRows) — because a row is one day of one SKU and every
// consumer sums a run of them: rounding before the sum drifts from rounding
// after it, which showed up as a billing column cents away from the cost column
// beside it. So sum first, round once, here. The same rule covers the CSV
// writer's `num` in lib/pivot.js, which cuts the same two decimals.
export const currencyExact = (value, code = CURRENCY_FALLBACK) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode(code),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value ?? 0);

export const number = (value) => new Intl.NumberFormat('en-US').format(value ?? 0);

// Metered quantities. Pinned to en-US like the currency helpers above so a
// comma-decimal browser cannot render "0,33" in the column beside a "$0.33".
export const quantity = (value) =>
  (value ?? 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

export const percent = (value) => `${value > 0 ? '+' : ''}${(value ?? 0).toFixed(1)}%`;

export const relativeTime = (iso) => {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const mins = Math.round(diff / 60000);
  if (Number.isNaN(mins)) return '';
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
};

// relativeTime for a timestamp that may not exist yet, returning '' when there is
// nothing to date. Three shapes mean "never": absent, Go's zero time (year 1,
// which marshals as 0001-01-01 and would render as "738000d ago"), and the epoch
// default a column upgrade leaves on rows written before it existed. A caller
// showing a snapshot's age needs all three to read as "never synced" rather than
// as an absurd age.
export const relativeAge = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t) || t <= 0) return '';
  // 2000-01-01. Nothing in this system predates it, so an earlier stamp is a
  // placeholder rather than a date.
  if (t < 946684800000) return '';
  return relativeTime(iso);
};

export const titleCase = (s = '') => s.charAt(0).toUpperCase() + s.slice(1);
